import { useTheme } from '@mui/material';
import { LineChart, lineClasses } from '@mui/x-charts';
import dayjs from 'dayjs';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { AccessEventBucket } from 'v2/hooks/useRecentAccessEvents/useRecentAccessEvents';
import ChartCard, { FULL_CHART_HEIGHT } from 'v2/pages/Dashboard/charts/ChartCard';
import { formatDate } from 'v2/utils/dateTime';

const GRANTED_SERIES_ID = 'granted';
const DENIED_SERIES_ID = 'denied';

// just enough for the widest tick label; the 45px default leaves a visible gap on the left
const Y_AXIS_WIDTH = 28;
// See DeviceOpeningsPerDay for why `top` cannot be smaller: the topmost y tick label is dropped
// when its height budget (its distance from the top edge) is too small for the caption.
// `right` is wider than that chart's 8px because a `point` scale puts the newest bucket exactly on
// the right edge, so half of its "HH:mm" label sits outside the plot and gets shortened to "1…".
const CHART_MARGIN = { top: 24, right: 20, bottom: 0, left: 0 };
// a quarter-width card has no room to label every bucket, so only every other whole hour is labelled
const TICK_HOUR_INTERVAL = 2;
// counts are whole events, so halves on the axis would be meaningless
const Y_AXIS_MIN_STEP = 1;
// the fills only hint at the volume; keeping them faint stops the two areas from muddying each other
const AREA_FILL_OPACITY = 0.15;
// Granted and denied have to use the theme's semantic green and red, but that pair is barely
// separable for the most common form of colour blindness (ΔE ~7 for deuteranopia, in both light
// and dark mode). The dashed denied line is the secondary encoding that makes the two series
// tellable apart without relying on hue.
const DENIED_LINE_DASH = '4 4';
// The legend sits under the plot but inside the chart's own box, so every pixel it takes is one the
// plot loses. It gets a 16px gap above it and none below — the card's own 16px padding already sets
// it off from the bottom edge — and the plot takes the rest of the card's chart area.
const LEGEND_GAP = 16;
// the single caption line the legend renders, at its `lineHeight: 100%`
const LEGEND_LABEL_HEIGHT = 12;
const LEGEND_HEIGHT = LEGEND_GAP + LEGEND_LABEL_HEIGHT;
const PLOT_HEIGHT = FULL_CHART_HEIGHT - LEGEND_HEIGHT;

interface Props {
  accessEventBuckets: AccessEventBucket[];
  isLoading: boolean;
}

const HourlyAccessEvents: React.FC<Props> = ({ accessEventBuckets, isLoading }) => {
  const { translation } = useTranslation();
  const theme = useTheme();

  // every bucket is filled in, quiet ones included, so an all-zero window means there is nothing to plot
  const hasEvents = accessEventBuckets.some(({ granted, denied }) => granted > 0 || denied > 0);

  return (
    <ChartCard
      title={translation.hourly_events}
      subtitle={translation.access_attempts_per_hour}
      isLoading={isLoading}
      isEmpty={!hasEvents}
      emptyStateContent={translation.dashboard_empty_hourly_events_content}
    >
      <LineChart
        height={PLOT_HEIGHT}
        margin={CHART_MARGIN}
        series={[
          {
            id: GRANTED_SERIES_ID,
            data: accessEventBuckets.map(({ granted }) => granted),
            color: theme.palette.success.main,
            label: translation.granted,
            area: true,
            showMark: false
          },
          {
            id: DENIED_SERIES_ID,
            data: accessEventBuckets.map(({ denied }) => denied),
            color: theme.palette.error.main,
            label: translation.denied,
            area: true,
            showMark: false
          }
        ]}
        xAxis={[
          {
            scaleType: 'point',
            data: accessEventBuckets.map(({ timestamp }) => timestamp),
            // the axis only has room for the time; the tooltip spells out the date as well, which
            // matters because the window rolls and the same time moves position as it does
            valueFormatter: (timestamp: string, { location }) =>
              formatDate(timestamp, location === 'tick' ? 'hourOnly' : 'dateTimeShort'),
            // Only whole hours get a label, and only every TICK_HOUR_INTERVAL-th one. Reading the
            // hour off the bucket itself keeps this independent of how many buckets make up an hour.
            tickInterval: (timestamp: string) => {
              const bucket = dayjs(timestamp);

              return bucket.minute() === 0 && bucket.hour() % TICK_HOUR_INTERVAL === 0;
            },
            disableTicks: true
          }
        ]}
        yAxis={[{ width: Y_AXIS_WIDTH, disableTicks: true, tickMinStep: Y_AXIS_MIN_STEP }]}
        grid={{ horizontal: true, vertical: true }}
        slotProps={{
          legend: {
            position: { vertical: 'bottom', horizontal: 'center' },
            sx: { marginBlockStart: `${LEGEND_GAP}px`, marginBlockEnd: 0 }
          }
        }}
        sx={{
          [`& .${lineClasses.area}`]: { fillOpacity: AREA_FILL_OPACITY },
          [`& .${lineClasses.line}[data-series="${DENIED_SERIES_ID}"]`]: { strokeDasharray: DENIED_LINE_DASH }
        }}
      />
    </ChartCard>
  );
};

export default HourlyAccessEvents;
